import type { ReactNode } from "react";
import "./Menu.css";

export interface MenuItem {
  key: string;
  label: ReactNode;
  /** grey second line under the label, e.g. "Paste another agency link" */
  description?: ReactNode;
  /** an <Icon /> */
  icon?: ReactNode;
  onClick: () => void;
  /** red text, e.g. Logout */
  danger?: boolean;
  /** greyed out and not clickable; stays hoverable so a tooltip can say why */
  disabled?: boolean;
  /** extra class, e.g. "hideOnDesktop" */
  className?: string;
}

export interface MenuProps {
  items: MenuItem[];
  /** person or workspace at the top */
  title?: ReactNode;
  subtitle?: ReactNode;
  /** an <Avatar /> next to the title */
  avatar?: ReactNode;
  /** under the actions, e.g. a language switch */
  footer?: ReactNode;
  /** class on the footer box, e.g. "hideOnDesktop" to hide it with its line */
  footerClassName?: string;
  className?: string;
}

/**
 * Dropdown panel with actions. The page decides where it opens.
 * Example: <Menu title="Laura" items={[{ key: "out", label: "Logout", onClick }]} />
 */
const Menu = ({
  items,
  title,
  subtitle,
  avatar,
  footer,
  footerClassName = "",
  className = "",
}: MenuProps) => (
  <div className={`ds-menu ${className}`.trim()} role="menu">
    {(title || avatar) && (
      <div className="ds-menu__header">
        {avatar}
        <span className="ds-menu__who">
          {title && <span className="ds-menu__name">{title}</span>}
          {subtitle && <span className="ds-menu__email">{subtitle}</span>}
        </span>
      </div>
    )}
    {items.map((item) => (
      <button
        key={item.key}
        type="button"
        role="menuitem"
        className={`ds-menu__item ${item.danger ? "ds-menu__item--danger" : ""} ${
          item.description ? "ds-menu__item--described" : ""
        } ${item.disabled ? "is-disabled" : ""} ${item.className ?? ""}`.trim()}
        aria-disabled={item.disabled || undefined}
        onClick={item.disabled ? undefined : item.onClick}
      >
        {item.icon}
        {item.description ? (
          <span className="ds-menu__text">
            <span className="ds-menu__label">{item.label}</span>
            <span className="ds-menu__description">{item.description}</span>
          </span>
        ) : (
          item.label
        )}
      </button>
    ))}
    {footer && (
      <div className={`ds-menu__footer ${footerClassName}`.trim()}>
        {footer}
      </div>
    )}
  </div>
);

export default Menu;
